/**
 * Domain Detection Utility
 *
 * Detects business from domain/subdomain
 * Supports:
 * - Subdomain: isletmeadi.sectorpilot.com.tr (veya .sectoras.com)
 * - Custom domain: www.isletmeadi.com / menu.isletmeadi.com
 * - Development: localhost:3000?businessId=xyz
 * - Firebase Hosting: prmanager-f881b.web.app
 */

import { db } from '../services/firebase';
import { collection, query, where, getDocs, getDoc, doc, limit } from 'firebase/firestore';

/**
 * Main domain configuration
 */
const CONFIG = {
  mainDomain: 'sectorpilot.com.tr',
  legacyDomains: ['sectorpilot.com', 'sectoras.com', 'sectoras.app'],
  devDomain: 'localhost',
  systemDomains: [
    'prmanager-f881b.web.app',
    'prmanager-f881b.firebaseapp.com',
    'madem-storefront.web.app',
    'madem-storefront.firebaseapp.com'
  ],
  reservedSubdomains: ['www', 'app', 'api', 'admin', 'panel', 'mail'],
  fallbackBusinessId: 'lezzet_emirgan', // Fallback for development
};

/**
 * ✅ IN-MEMORY CACHE: Domain tespiti sonuçlarını önbelleğe al.
 */
const _domainCache = new Map(); // hostname → { result, timestamp }
const _inFlight = new Map();    // hostname → Promise (çakışan çağrıları deduplicate et)
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 dakika


/**
 * Parse hostname to extract subdomain
 * @param {string} hostname - window.location.hostname
 * @returns {object} { isSubdomain, subdomain, isCustomDomain, isSystemDomain }
 */
export const parseHostname = (hostname) => {
  const cleanHost = (hostname || '').toLowerCase();

  // Development environment (localhost)
  if (cleanHost === CONFIG.devDomain || cleanHost.includes('localhost') || cleanHost === '127.0.0.1') {
    return {
      isDevelopment: true,
      isSubdomain: false,
      isCustomDomain: false,
      isSystemDomain: true,
      subdomain: null,
    };
  }

  // Check if it's a system domain (Firebase hosting)
  if (CONFIG.systemDomains.some(d => cleanHost.includes(d))) {
    return {
      isDevelopment: false,
      isSubdomain: false,
      isCustomDomain: false,
      isSystemDomain: true,
      subdomain: null,
    };
  }

  // Check if it's sectorpilot.com.tr or a subdomain of sectorpilot.com.tr
  const allPlatformDomains = [CONFIG.mainDomain, ...CONFIG.legacyDomains];
  for (const baseDom of allPlatformDomains) {
    if (cleanHost === baseDom || cleanHost === `www.${baseDom}`) {
      return {
        isDevelopment: false,
        isSubdomain: false,
        isCustomDomain: false,
        isSystemDomain: true,
        subdomain: null,
      };
    }

    if (cleanHost.endsWith(`.${baseDom}`)) {
      const subPart = cleanHost.slice(0, -(baseDom.length + 1));
      const firstLabel = subPart.split('.')[0];
      if (firstLabel && !CONFIG.reservedSubdomains.includes(firstLabel)) {
        return {
          isDevelopment: false,
          isSubdomain: true,
          isCustomDomain: false,
          isSystemDomain: false,
          subdomain: firstLabel,
        };
      }
      return {
        isDevelopment: false,
        isSubdomain: false,
        isCustomDomain: false,
        isSystemDomain: true,
        subdomain: null,
      };
    }
  }

  // Custom domain (not platform domain and not system domain)
  return {
    isDevelopment: false,
    isSubdomain: false,
    isCustomDomain: true,
    isSystemDomain: false,
    customDomain: cleanHost,
  };
};

/**
 * Get business ID from subdomain or slug
 * @param {string} subdomain - subdomain or business slug
 * @returns {Promise<string|null>} businessId
 */
export const getBusinessIdFromSubdomain = async (subdomain) => {
  try {
    if (!subdomain) return null;
    let normalized = subdomain.trim().toLowerCase();

    // 🚀 MADEM ALIAS / SLUG HARDENED MAPPING
    if (normalized === 'madem' || normalized === 'madem-tatli' || normalized === 'mademtatli') {
      const knownIds = ['business_fb_mkpfltqu_it85p', 'business_fb_madem_tatli', 'madem'];
      for (const kId of knownIds) {
        const docRef = doc(db, 'businesses', 'food_beverage', 'businesses', kId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          return kId;
        }
      }
    }

    // 1. First try: Direct Document ID check
    const bizRef = doc(db, 'businesses', 'food_beverage', 'businesses', normalized);
    const bizSnap = await getDoc(bizRef);
    if (bizSnap.exists()) {
      return normalized;
    }

    // 2. Second try: Query businesses by slug or domains.slug
    const businessesRef = collection(db, 'businesses', 'food_beverage', 'businesses');
    const q = query(businessesRef, where('slug', '==', normalized));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }

    const qDomSlug = query(businessesRef, where('domains.slug', '==', normalized));
    const snapDomSlug = await getDocs(qDomSlug);
    if (!snapDomSlug.empty) {
      return snapDomSlug.docs[0].id;
    }

    return null;
  } catch (error) {
    console.error('❌ Error fetching business from subdomain/slug:', error);
    return null;
  }
};

/**
 * FAST LOOKUP: Search for business by alias (slug) in all sectors
 * @param {string} alias - Business code/slug
 * @returns {Promise<{id: string, sector: string}|null>}
 */
export const lookupBusinessByAlias = async (alias) => {
  if (!alias) return null;
  let normalizedAlias = alias.trim().toLowerCase();
  
  if (normalizedAlias === 'madem' || normalizedAlias === 'madem-tatli' || normalizedAlias === 'mademtatli') {
    const knownIds = ['business_fb_mkpfltqu_it85p', 'business_fb_madem_tatli', 'madem'];
    for (const kId of knownIds) {
      const docRef = doc(db, 'businesses', 'food_beverage', 'businesses', kId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: kId, sector: 'food_beverage' };
      }
    }
  }

  const sectors = [
    'food_beverage', 'food-beverage', 'retail', 'service', 
    'restaurants', '7hXb9mxWP8e1dtP2suvi', 'VD0Ndssw0KD34ZoHnRTV', 'GaGHwMSCFa3wFOQyhdtP'
  ];

  try {
    for (const sector of sectors) {
      try {
        const businessesRef = collection(db, 'businesses', sector, 'businesses');
        
        // 1. Try by slug
        const qSlug = query(businessesRef, where('slug', '==', normalizedAlias), limit(1));
        const snapSlug = await getDocs(qSlug);
        if (!snapSlug.empty) {
          return { id: snapSlug.docs[0].id, sector };
        }

        // 2. Try by document ID
        const docRef = doc(db, 'businesses', sector, 'businesses', normalizedAlias);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          return { id: normalizedAlias, sector };
        }
      } catch (e) {}
    }
    return null;
  } catch (error) {
    console.error('Error in lookupBusinessByAlias:', error);
    return null;
  }
};


/**
 * Get business ID from custom domain
 * @param {string} customDomain - custom domain
 * @returns {Promise<string|null>} businessId
 */
export const getBusinessIdFromCustomDomain = async (customDomain) => {
  try {
    const normalizedHost = customDomain.toLowerCase();
    const domainWithoutPrefix = normalizedHost.replace(/^(www\.|menu\.|reservation\.|restaurant\.|qr\.)/, '');
    
    const sectors = [
      'food_beverage', 'food-beverage', 'retail', 'service', 
      'restaurants', '7hXb9mxWP8e1dtP2suvi', 'VD0Ndssw0KD34ZoHnRTV', 'GaGHwMSCFa3wFOQyhdtP'
    ];

    const lookups = sectors.map(async (sector) => {
      try {
        const businessesRef = collection(db, 'businesses', sector, 'businesses');
        const snapshot = await getDocs(businessesRef);
        
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          const domains = data.domains || {};
          const customDomains = domains.customDomains || [];
          const mappings = data.domainMappings || [];

          const hasMapping = mappings.some(m => m.domain?.toLowerCase() === normalizedHost || m.domain?.toLowerCase() === domainWithoutPrefix);
          const hasLegacy = customDomains.some(d => {
            const lowD = d.toLowerCase();
            return lowD === normalizedHost || lowD === domainWithoutPrefix || lowD.replace(/^(www\.|menu\.|reservation\.|restaurant\.)/, '') === domainWithoutPrefix;
          });

          if (hasMapping || hasLegacy) return docSnap.id;
        }
      } catch (e) { /* skip */ }
      return null;
    });

    const results = await Promise.all(lookups);
    return results.find(r => r !== null) || null;
  } catch (error) {
    console.error('❌ Error fetching business from custom domain:', error);
    return null;
  }
};

/**
 * Get business ID from URL search params (fallback)
 * @param {URLSearchParams} searchParams - URL search params
 * @returns {string|null} businessId
 */
export const getBusinessIdFromParams = (searchParams) => {
  const businessId = searchParams.get('businessId');
  if (businessId) {
    return businessId;
  }
  return null;
};

/**
 * Detect brand and business from custom domain or subdomain
 * @param {string} hostname - hostname
 * @returns {Promise<object|null>} { businessId, brandSlug, businessSlug }
 */
export const detectBrandFromDomain = async (hostname) => {
  const normalizedHost = (hostname || '').toLowerCase();
  const cacheKey = `brand_domain_cache_${normalizedHost}`;

  // Ana SaaS paneli (sectorpilot.com.tr veya www.sectorpilot.com.tr) ise direkt null dön (SaaS arayüzü açılsın)
  const parsedHost = parseHostname(normalizedHost);
  if (parsedHost.isSystemDomain && !parsedHost.isDevelopment && !parsedHost.isSubdomain) {
    return null;
  }

  // ✅ 1. LOCALSTORAGE CACHE HIT
  const isDevHost = normalizedHost === 'localhost' || normalizedHost === '127.0.0.1' || normalizedHost.includes('localhost');
  if (!isDevHost) {
    try {
      const cachedString = localStorage.getItem(cacheKey);
      if (cachedString) {
        const cachedData = JSON.parse(cachedString);
        if (cachedData.result && cachedData.result.businessId && (Date.now() - cachedData.timestamp) < 3600000) {
          return cachedData.result;
        } else {
          localStorage.removeItem(cacheKey);
        }
      }
    } catch (e) { /* ignore */ }
  } else {
    try {
      localStorage.removeItem(cacheKey);
    } catch (e) {}
  }

  // ✅ 2. MEMORY CACHE HIT
  const cached = _domainCache.get(normalizedHost);
  if (cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS) {
    return cached.result;
  }

  // ✅ 3. IN-FLIGHT DEDUPLICATION
  if (_inFlight.has(normalizedHost)) {
    return _inFlight.get(normalizedHost);
  }

  const detectionPromise = (async () => {
    try {
      const parsed = parseHostname(normalizedHost);

      // 🚀 LOCALHOST / DEVELOPMENT CONTROL
      if (parsed.isDevelopment) {
        const urlParams = new URLSearchParams(window.location.search);
        const brandParam = urlParams.get('brand') || urlParams.get('brandSlug');
        const bizParam = urlParams.get('businessId') || urlParams.get('bizSlug');
        const isMenuRoute = window.location.pathname.startsWith('/menu/') || window.location.pathname.startsWith('/qr/');

        if (brandParam || bizParam || isMenuRoute) {
          let targetBrandSlug = brandParam || 'all';
          if (brandParam === 'madem') targetBrandSlug = 'all';

          const devResult = {
            businessId: 'business_fb_mkpfltqu_it85p',
            brandSlug: targetBrandSlug,
            sector: 'food_beverage',
            businessSlug: 'madem'
          };
          return devResult;
        }

        _domainCache.set(normalizedHost, { result: null, timestamp: Date.now() });
        return null;
      }

      // 🚀 FAST-PATH: Direct O(1) Lookup from global `domains` collection
      try {
        const domainDocRef = doc(db, 'domains', normalizedHost);
        const domainDocSnap = await getDoc(domainDocRef);
        if (domainDocSnap.exists()) {
          const dData = domainDocSnap.data();
          if (dData.businessId) {
            const fastResult = {
              businessId: dData.businessId,
              brandSlug: dData.brandSlug || dData.brandId || 'all',
              sector: dData.sector || dData.type || 'food_beverage',
              businessSlug: dData.slug || dData.businessId
            };

            _domainCache.set(normalizedHost, { result: fastResult, timestamp: Date.now() });
            try {
              localStorage.setItem(cacheKey, JSON.stringify({ result: fastResult, timestamp: Date.now() }));
            } catch (e) {}

            return fastResult;
          }
        }
      } catch (fastErr) {
        console.warn('O(1) domain lookup error, falling back:', fastErr);
      }

      const domainWithoutPrefix = normalizedHost.replace(/^(www\.|menu\.|reservation\.|restaurant\.|qr\.)/, '');

      // 🚀 MADEM DOMAIN FAST-PATH FALLBACK
      if (normalizedHost.includes('madem') || domainWithoutPrefix.includes('madem')) {
        const mademResult = {
          businessId: 'business_fb_mkpfltqu_it85p',
          brandSlug: 'all',
          sector: 'food_beverage',
          businessSlug: 'business_fb_mkpfltqu_it85p'
        };
        _domainCache.set(normalizedHost, { result: mademResult, timestamp: Date.now() });
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ result: mademResult, timestamp: Date.now() }));
        } catch (e) {}
        return mademResult;
      }

      const sectors = [
        'food_beverage',
        'food-beverage',
        'retail',
        'service',
        'restaurants'
      ];

      const sectorLookups = sectors.map(async (sector) => {
        try {
          const bizRef = collection(db, 'businesses', sector, 'businesses');
          
          // 🚀 1. Slug/Subdomain üzerinden hızlı ara (örn: zarifzade.sectorpilot.com.tr)
          const subdomain = parsed.isSubdomain ? parsed.subdomain : normalizedHost.split('.')[0];
          if (subdomain && !CONFIG.reservedSubdomains.includes(subdomain) && subdomain !== 'menu') {
            const q = query(bizRef, where('slug', '==', subdomain), limit(1));
            const s = await getDocs(q);
            if (!s.empty) {
              const docData = s.docs[0].data();
              return { 
                businessId: s.docs[0].id, 
                brandSlug: 'all', 
                sector, 
                businessSlug: docData.slug || s.docs[0].id 
              };
            }

            const qDomSlug = query(bizRef, where('domains.slug', '==', subdomain), limit(1));
            const sDomSlug = await getDocs(qDomSlug);
            if (!sDomSlug.empty) {
              const docData = sDomSlug.docs[0].data();
              return { 
                businessId: sDomSlug.docs[0].id, 
                brandSlug: 'all', 
                sector, 
                businessSlug: docData.domains?.slug || docData.slug || sDomSlug.docs[0].id 
              };
            }
          }

          // 🚀 2. Custom Domains dizisi üzerinden ara
          const qC = query(bizRef, where('domains.customDomains', 'array-contains', normalizedHost), limit(1));
          const sC = await getDocs(qC);
          if (!sC.empty) {
            const docSnap = sC.docs[0];
            const data = docSnap.data();
            const m = (data.domainMappings || []).find(x => x.domain?.toLowerCase() === normalizedHost || x.domain?.toLowerCase() === domainWithoutPrefix);
            return { 
              businessId: docSnap.id, 
              brandSlug: m?.brandSlug || 'all', 
              sector, 
              businessSlug: data.slug || docSnap.id 
            };
          }

          // 🚀 3. Prefix'siz domain ara
          if (domainWithoutPrefix !== normalizedHost) {
            const qP = query(bizRef, where('domains.customDomains', 'array-contains', domainWithoutPrefix), limit(1));
            const sP = await getDocs(qP);
            if (!sP.empty) {
              const docSnap = sP.docs[0];
              const data = docSnap.data();
              const m = (data.domainMappings || []).find(x => x.domain?.toLowerCase() === normalizedHost || x.domain?.toLowerCase() === domainWithoutPrefix);
              return { 
                businessId: docSnap.id, 
                brandSlug: m?.brandSlug || 'all', 
                sector, 
                businessSlug: data.slug || docSnap.id 
              };
            }
          }

          // 🚀 4. Fallback (Mappings objeleri için tarama)
          const snapshot = await getDocs(query(bizRef, limit(20)));
          for (const docSnap of snapshot.docs) {
            const data = docSnap.data();
            const m = (data.domainMappings || []).find(x => x.domain?.toLowerCase() === normalizedHost || x.domain?.toLowerCase() === domainWithoutPrefix);
            if (m) {
              const businessId = docSnap.id;
              const foundData = { 
                businessId, 
                brandSlug: m.brandSlug || 'all', 
                sector, 
                businessSlug: data.slug || businessId 
              };
              try {
                localStorage.setItem(cacheKey, JSON.stringify({ result: foundData, timestamp: Date.now() }));
              } catch (e) {}
              return foundData;
            }
          }
        } catch (e) { console.error(`Error in ${sector}:`, e); }
        return null;
      });

      const results = await Promise.race([
        Promise.all(sectorLookups),
        new Promise(resolve => setTimeout(() => resolve([]), 8000))
      ]);

      const found = results.find(r => r !== null) || null;

      if (found) {
        _domainCache.set(normalizedHost, { result: found, timestamp: Date.now() });
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ result: found, timestamp: Date.now() }));
        } catch (e) {}
      }
      
      return found;
    } catch (error) {
      console.error('❌ Error in detectBrandFromDomain:', error);
      return null;
    } finally {
      _inFlight.delete(normalizedHost);
    }
  })();

  _inFlight.set(normalizedHost, detectionPromise);
  return detectionPromise;
};


export const detectBusinessFromDomain = async (hostname, searchParams) => {
  const parsed = parseHostname(hostname);

  if (parsed.isDevelopment || parsed.isSystemDomain) {
    const paramsBusinessId = getBusinessIdFromParams(searchParams);
    if (paramsBusinessId) return paramsBusinessId;
    if (parsed.isDevelopment) return CONFIG.fallbackBusinessId;
    return null;
  }

  const brandData = await detectBrandFromDomain(hostname);
  if (brandData && brandData.businessId) {
    return brandData.businessId;
  }

  if (parsed.isSubdomain) {
    const businessId = await getBusinessIdFromSubdomain(parsed.subdomain);
    if (businessId) return businessId;
  }

  const paramsBusinessId = getBusinessIdFromParams(searchParams);
  if (paramsBusinessId) return paramsBusinessId;

  return null;
};

/**
 * Get domain configuration for a business
 * @param {string} businessId - business ID
 * @returns {Promise<object>} domain configuration
 */
export const getBusinessDomainConfig = async (businessId) => {
  try {
    const businessRef = doc(db, 'businesses', 'food_beverage', 'businesses', businessId);
    const businessSnap = await getDoc(businessRef);

    if (!businessSnap.exists()) {
      return null;
    }

    const data = businessSnap.data();
    const domains = data.domains || {};
    const slug = domains.slug || data.slug || businessId;

    return {
      businessId,
      slug: slug,
      primaryDomain: domains.primaryDomain || `${slug}.${CONFIG.mainDomain}`,
      customDomains: domains.customDomains || [],
      verified: domains.verified || false,
      sslEnabled: domains.sslEnabled !== false,
    };
  } catch (error) {
    console.error('❌ Error fetching domain config:', error);
    return null;
  }
};

/**
 * Generate QR code URL for business
 * @param {string} businessId - business ID
 * @param {boolean} useCustomDomain - use custom domain if available
 * @returns {Promise<string>} QR menu URL
 */
export const generateQRMenuUrl = async (businessId, useCustomDomain = false) => {
  try {
    const domainConfig = await getBusinessDomainConfig(businessId);

    if (!domainConfig) {
      return `https://${CONFIG.mainDomain}/qr/menu?businessId=${businessId}`;
    }

    if (useCustomDomain && domainConfig.customDomains.length > 0) {
      const customDomain = domainConfig.customDomains[0];
      return `https://${customDomain}`;
    }

    return `https://${domainConfig.primaryDomain}`;
  } catch (error) {
    console.error('❌ Error generating QR menu URL:', error);
    return `https://${CONFIG.mainDomain}/qr/menu?businessId=${businessId}`;
  }
};

export const validateDomainDNS = async (customDomain) => {
  return {
    valid: false,
    message: 'DNS validation requires backend implementation',
    cnameRecord: `${customDomain} CNAME madem-storefront.web.app`,
    aRecord: '199.36.158.100',
    verified: false,
  };
};

export default {
  parseHostname,
  detectBusinessFromDomain,
  getBusinessIdFromSubdomain,
  getBusinessIdFromCustomDomain,
  getBusinessDomainConfig,
  generateQRMenuUrl,
  validateDomainDNS,
  CONFIG,
};
